import { Injectable } from '@angular/core';
import {
  API_URL_OVERRIDE_REGISTRY,
  ApiUrlOverrideRegistryEntry,
  OverridableApiKey,
} from './api-url-registry';
import {
  ActiveOverride,
  buildUrl,
  clearAllOverrides,
  detectPrNumber,
  getActiveOverrides,
  getCurrentDefaultUrl,
  getOverrideMap,
  isOverrideAllowed,
  OverrideMap,
  setTarget,
} from './api-url-override.store';

/**
 * Thin Angular wrapper over the framework-agnostic override store. Used by the
 * "Override APIs" dialog and the layout footer banner. Also installs the
 * `window.__apiUrlOverride` agent helper at construction.
 *
 * The HTTP chokepoint (`MyHttpClientFactory`) does NOT use this service — it calls
 * `resolveApiUrlOverride()` from the store directly, so resolution has no DI
 * timing dependency on this service being instantiated first.
 */
@Injectable({ providedIn: 'root' })
export class ApiUrlOverrideService {
  readonly registry: ApiUrlOverrideRegistryEntry[] = API_URL_OVERRIDE_REGISTRY;

  /** localStorage key for the dialog's "reload page on change" preference. */
  private static readonly RELOAD_PREF_KEY =
    'ups.apiUrlOverrides.reloadOnChange';

  constructor() {
    this.installAgentHelper();
  }

  /** Whether the override UI/channels are enabled (localhost or PR host). */
  get allowed(): boolean {
    return isOverrideAllowed();
  }

  /**
   * Dialog preference: also reload the page after a Save/Reset. Off by default —
   * overrides apply live to factory traffic and fresh reads without a reload; a
   * reload is only needed to repoint components that captured `environment.urls`
   * once at init (e.g. a Syncfusion DataManager `host`). Persisted per browser.
   */
  get reloadOnChange(): boolean {
    try {
      return (
        typeof window !== 'undefined' &&
        window.localStorage.getItem(ApiUrlOverrideService.RELOAD_PREF_KEY) ===
          '1'
      );
    } catch {
      return false;
    }
  }

  set reloadOnChange(value: boolean) {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          ApiUrlOverrideService.RELOAD_PREF_KEY,
          value ? '1' : '0'
        );
      }
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }

  /** Detected PR number from the current host, or null on local dev. */
  get detectedPrNumber(): string | null {
    return detectPrNumber();
  }

  getActiveOverrides(): ActiveOverride[] {
    return getActiveOverrides();
  }

  getOverrideMap(): OverrideMap {
    return getOverrideMap();
  }

  getCurrentDefaultUrl(apiKey: OverridableApiKey): string | undefined {
    return getCurrentDefaultUrl(apiKey);
  }

  /** Preview the URL a target string would resolve to (for the dialog's live hint). */
  previewUrl(
    entry: ApiUrlOverrideRegistryEntry,
    target: string
  ): string | null {
    return buildUrl(entry, target).url;
  }

  setTarget(apiKey: OverridableApiKey, target: string): void {
    setTarget(apiKey, target);
  }

  clearAll(): void {
    clearAllOverrides();
  }

  reload(): void {
    if (typeof window !== 'undefined') window.location.reload();
  }

  private installAgentHelper(): void {
    if (typeof window === 'undefined' || !isOverrideAllowed()) return;
    (window as unknown as Record<string, unknown>)['__apiUrlOverride'] = {
      list: () => getActiveOverrides(),
      targets: () =>
        API_URL_OVERRIDE_REGISTRY.map((e) => ({
          apiKey: e.apiKey,
          default: getCurrentDefaultUrl(e.apiKey),
          local: e.localUrl,
          stg: e.stgUrl,
          prod: e.prodUrl,
          pr: e.prSlug ? `pr-<number>` : undefined,
        })),
      set: (apiKey: OverridableApiKey, target: string) => {
        setTarget(apiKey, target);
        return getActiveOverrides();
      },
      clear: () => {
        clearAllOverrides();
        return getActiveOverrides();
      },
      reload: () => this.reload(),
    };
  }
}
